import React, { useState, useRef, useEffect } from 'react';
import styles from './index.module.scss';
import { useScreenSlideIn } from '@/hooks/useScreenSlideIn';
import card1VideoSrc from '@/assets/home/screen2-video/card1.mp4';
import card2VideoSrc from '@/assets/home/screen2-video/card2.mp4';
import card3VideoSrc from '@/assets/home/screen2-video/card3.mp4';
import card4VideoSrc from '@/assets/home/screen2-video/card4.mp4';

const LEAVE_IGNORE_MS = 50;
const LEAVE_CHAIN_APPLY_MS = 10;

export default function Screen2() {
  const [activeCard, setActiveCard] = useState<number>(2);
  const leavingRef = useRef(false);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cardsContainerRef = useRef<HTMLDivElement>(null);
  const leaveStartedFromRef = useRef<number | null>(null);
  const leaveChainTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCardRef = useRef<number | null>(null);
  const titleSlideInRef = useScreenSlideIn({
    triggerSlideRatio: 0.8,
    animationClassName: styles['screen2-title-anim-start'],
    triggerOnce: true,
  });
  const cardsSlideInRef = useScreenSlideIn({
    triggerSlideRatio: 1,
    animationClassName: styles['screen2-cards-anim-start'],
    skipInitialCheck: true,
    triggerOnce: true,
  });
  const card1VideoRef = useRef<HTMLVideoElement>(null);
  const card2VideoRef = useRef<HTMLVideoElement>(null);
  const card3VideoRef = useRef<HTMLVideoElement>(null);
  const card4VideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    [card1VideoRef, card2VideoRef, card3VideoRef, card4VideoRef].forEach((ref) => {
      const video = ref.current;
      if (video) video.load();
    });
  }, []);

  useEffect(() => {
    const refs = [
      { ref: card1VideoRef, card: 1 },
      { ref: card2VideoRef, card: 2 },
      { ref: card3VideoRef, card: 3 },
      { ref: card4VideoRef, card: 4 },
    ];
    refs.forEach(({ ref, card }) => {
      const video = ref.current;
      if (!video) return;
      if (activeCard === card) {
        video.play().catch(() => {});
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, [activeCard]);

  const handleCardEnter = (card: number) => {
    const leaving = leavingRef.current;
    const inLeaveChain = leaveStartedFromRef.current != null;
    if (leaving) return;
    if (inLeaveChain) {
      if (leaveChainTimerRef.current) clearTimeout(leaveChainTimerRef.current);
      pendingCardRef.current = card;
      leaveChainTimerRef.current = setTimeout(() => {
        if (pendingCardRef.current != null) {
          setActiveCard(pendingCardRef.current);
          pendingCardRef.current = null;
        }
        leaveStartedFromRef.current = null;
        leaveChainTimerRef.current = null;
      }, LEAVE_CHAIN_APPLY_MS);
      return;
    }
    setActiveCard(card);
  };

  const setLeaving = () => {
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    leavingRef.current = true;
    leaveTimerRef.current = setTimeout(() => {
      leavingRef.current = false;
      leaveTimerRef.current = null;
    }, LEAVE_IGNORE_MS);
  };

  const handleCardsEnter = () => {
    if (leaveChainTimerRef.current) {
      clearTimeout(leaveChainTimerRef.current);
      leaveChainTimerRef.current = null;
      pendingCardRef.current = null;
    }
    leaveStartedFromRef.current = null;
  };

  const handleCardsLeave = () => {
    if (leaveChainTimerRef.current) {
      clearTimeout(leaveChainTimerRef.current);
      leaveChainTimerRef.current = null;
      pendingCardRef.current = null;
    }
    if (leaveStartedFromRef.current != null) {
      setActiveCard(leaveStartedFromRef.current);
      leaveStartedFromRef.current = null;
    }
    setLeaving();
  };

  const handleCardLeave = (e: React.MouseEvent) => {
    const container = cardsContainerRef.current;
    const related = e.relatedTarget as Node | null;
    const isEnteringAnotherCard = related && container?.contains(related) && related !== container;
    const isGoingOutside = !container || !related || !isEnteringAnotherCard;
    if (isEnteringAnotherCard && leaveStartedFromRef.current === null) {
      leaveStartedFromRef.current = activeCard;
    }
    const reallyLeaving = isGoingOutside && related !== container;
    if (reallyLeaving) {
      if (leaveChainTimerRef.current) {
        clearTimeout(leaveChainTimerRef.current);
        leaveChainTimerRef.current = null;
        pendingCardRef.current = null;
      }
      setLeaving();
    }
  };

  return (
    <section className={styles['screen2-cover']} ref={titleSlideInRef.ref}>
      <div className={styles['screen2-wrap']}>
        <div className={styles['screen2-title-container']}>
          <h2 className={styles['screen2-title']}>AI学习智能体新范式</h2>
        </div>

        <div
          ref={(el) => {
            (cardsContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
            (cardsSlideInRef.ref as React.MutableRefObject<HTMLElement | null>).current = el;
          }}
          className={styles['screen2-cards']}
          onMouseEnter={handleCardsEnter}
          onMouseLeave={handleCardsLeave}
        >
          <div
            className={`${styles['screen2-card']} ${styles['screen2-card1']} ${
              activeCard === 1 ? styles['active'] : styles['inactive']
            }`}
            onMouseEnter={() => handleCardEnter(1)}
            onMouseLeave={handleCardLeave}
          >
            <div
              className={`${styles['screen2-card-image']} ${styles['screen2-card-image-with-video']}`}
            >
              <video
                ref={card1VideoRef}
                className={styles['screen2-card-video']}
                src={card1VideoSrc}
                muted
                loop
                playsInline
                preload="auto"
              />
            </div>
            <div className={styles['screen2-card-content']}>
              <h3 className={styles['screen2-card-title']}>真人级</h3>
              <p className={styles['screen2-card-desc']}>
                {activeCard === 1
                  ? '毫秒响应、情感理解、多模态融合的类人交互体验'
                  : '类人交互体验'.slice(0, 6)}
              </p>
            </div>
          </div>

          <div
            className={`${styles['screen2-card']} ${styles['screen2-card2']} ${
              activeCard === 2 ? styles['active'] : styles['inactive']
            }`}
            onMouseEnter={() => handleCardEnter(2)}
            onMouseLeave={handleCardLeave}
          >
            <div
              className={`${styles['screen2-card-image']} ${styles['screen2-card-image-with-video']}`}
            >
              <video
                ref={card2VideoRef}
                className={styles['screen2-card-video']}
                src={card2VideoSrc}
                muted
                loop
                playsInline
                preload="auto"
              />
            </div>
            <div className={styles['screen2-card-content']}>
              <h3 className={styles['screen2-card-title']}>大师级</h3>
              <p className={styles['screen2-card-desc']}>
                {activeCard === 2
                  ? '沉淀顶尖教育专家方法论,确保专业深度与权威性'
                  : '沉淀顶尖教育专家方法论,确保专业深度与权威性'.slice(0, 6)}
              </p>
            </div>
          </div>

          <div
            className={`${styles['screen2-card']} ${styles['screen2-card3']} ${
              activeCard === 3 ? styles['active'] : styles['inactive']
            }`}
            onMouseEnter={() => handleCardEnter(3)}
            onMouseLeave={handleCardLeave}
          >
            <div
              className={`${styles['screen2-card-image']} ${styles['screen2-card-image-with-video']}`}
            >
              <video
                ref={card3VideoRef}
                className={styles['screen2-card-video']}
                src={card3VideoSrc}
                muted
                loop
                playsInline
                preload="auto"
              />
            </div>
            <div className={styles['screen2-card-content']}>
              <h3 className={styles['screen2-card-title']}>专属级</h3>
              <p className={styles['screen2-card-desc']}>
                {activeCard === 3
                  ? '通过持续互动记忆与适配，实现终身成长的个性化陪伴'
                  : '个性化陪伴'.slice(0, 6)}
              </p>
            </div>
          </div>

          <div
            className={`${styles['screen2-card']} ${styles['screen2-card4']} ${
              activeCard === 4 ? styles['active'] : styles['inactive']
            }`}
            onMouseEnter={() => handleCardEnter(4)}
            onMouseLeave={handleCardLeave}
          >
            <div
              className={`${styles['screen2-card-image']} ${styles['screen2-card-image-with-video']}`}
            >
              <video
                ref={card4VideoRef}
                className={styles['screen2-card-video']}
                src={card4VideoSrc}
                muted
                loop
                playsInline
                preload="auto"
              />
            </div>
            <div className={styles['screen2-card-content']}>
              <h3 className={styles['screen2-card-title']}>更普惠</h3>
              <p className={styles['screen2-card-desc']}>
                {activeCard === 4 ? '更强的技术、更多的学员、更低的价格' : '更强的技术'.slice(0, 6)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
